import { Image, Flex, Text, Box } from 'rebass';
import { DateTime } from 'luxon';
import { Field, Formik } from 'formik';
import { connect } from 'react-redux';
import { isEmpty, isString, isEqual, isObject } from 'lodash';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import fileExtension from 'file-extension';
import store from 'store';
import { IoMdClose } from "react-icons/io";
import { BsBookmarkStar } from "react-icons/bs";
import { ReactComponent as BabysFYLabel } from 'babys-first-year/assets/babyyearbookslabel.svg';

import { DatePicker } from '../../../app/form/formik';
import { bodyWithLockVersion, getJSDateFromISO } from '../../../app/form/helper';
import { createStoryCoverMedium } from '../../../story-media/actions';
import { updateStory } from '../../actions';
import { useEntity } from '../../../entities/hooks';
import { useHistory, useMatch } from '../../../app/hooks';
import { useStoryImages, useStoryWithJournalId } from '../../hooks';
import CoverPicker from './picker';
import Main from '../main';
import NewStoryHeader from '../header';
import TextField from '../../../app/form/formik/text';
import NewStoryFooter from '../footer';
import useLocation from 'app/hooks/location';
import { useFirstTimeUserFlowVisible } from 'stories/hooks/index';
import Modal from 'app/modal/v3/index';
import { LetsBeginFooterComponent } from '../global/lets-begin';
import { getPublicChildJournal } from 'child-journals/api';
import { useStorySWR } from 'stories/api';

const getTitle = (title = '', dateTitle, beganOnDate) => {
  return title;
  const beganOnTitle = DateTime.fromISO(beganOnDate).toLocaleString(DateTime.DATE_FULL);
  if (beganOnTitle !== dateTitle) return beganOnTitle;
  return dateTitle;
};

const AUTOSAVE_DELAY = 800;

// Debounced background save for title/began-on/ended-on, so leaving this screen
// any way other than the Back / Review & Edit buttons (a sidebar link, browser
// back, closing the tab) doesn't silently discard what was typed. Cover photo
// upload stays tied to the explicit submit actions — it's a heavier fetch+blob+
// upload, not something worth repeating on every keystroke.
//
// Rendered as its own component (rather than inline in the Formik render-prop
// function) so its hooks follow the normal rules of hooks.
//
// Gated on Formik's own `dirty` flag rather than a custom "did the user touch
// this" ref: `dirty` is false right after `enableReinitialize` syncs values from
// freshly (re)loaded story data — including the reload this same autosave
// triggers — so it already tells apart a real edit from the values simply
// mirroring the server, with no extra bookkeeping needed.
const AutoSave = ({ values, dirty, onSave }) => {
  const debounceRef = useRef(null);
  const valuesRef = useRef(values);
  const onSaveRef = useRef(onSave);
  valuesRef.current = values;
  onSaveRef.current = onSave;

  useEffect(() => {
    if (!dirty) return undefined;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSaveRef.current(valuesRef.current);
    }, AUTOSAVE_DELAY);
    return () => clearTimeout(debounceRef.current);
  }, [dirty, values.title, values['began-on'], values['ended-on']]);

  return null;
};

const NewStoryCover = props => {
  const {
    params: { id },
  } = useMatch();
  const { replace } = useHistory();
  const { pathname } = useLocation();
  const images = useStoryImages({ id });
  const { entity } = useEntity({ type: 'stories', id });
  const { data: storyData, revalidate: revalidateStory } = useStorySWR(id);
  const story = storyData?.data?.data?.attributes;

  const [hideEndDate, setHideEndDate] = useState(true)
  const [member, setMember] = useState(null)
  const memoirDetailId = entity?.['journal-ids']?.[0]

  const getMemoirDetails = async (memoirDetailId) => {
    const response = await getPublicChildJournal(memoirDetailId);
    if (response.data?.initial?.included?.length) {
      setMember(response.data?.initial?.included.find(({ type }) => type === "members")?.attributes)
    }
  }

  useEffect(() => {
    if (memoirDetailId)
      getMemoirDetails(memoirDetailId)
  }, [memoirDetailId])

  const isFirstTimeUser = useFirstTimeUserFlowVisible()
  const [firstTimeUserModal, setFirstTimeUserModal] = useState(false)
  const isFromTellAStory = pathname.includes('/tell_a_story/') && !sessionStorage.getItem('fromMarcom')

  const ftui_watched = store.get('story_header_ftui_watched');

  useEffect(() => {
    setFirstTimeUserModal(!ftui_watched || isFirstTimeUser || pathname.includes('/tell_a_story/'))
  }, [isFirstTimeUser])

  const getNextRoute = () => {
    replace(`/${pathname?.includes('tell_a_story') ? 'tell_a_story/story' : 'story'}/${id}/edit`);
  };

  // Background save for AutoSave — see its comment above for why this stays
  // narrower than the full onSubmit (no cover upload, no navigation).
  const autosave = async ({ title, 'began-on': beganOn, 'ended-on': endedOn }) => {
    try {
      const body = bodyWithLockVersion(story, { title, 'began-on': beganOn, 'ended-on': endedOn });
      await props.updateStory(id, body);
      revalidateStory();
    } catch (err) {
      // Best-effort background save — a failure here shouldn't interrupt typing;
      // the explicit Back / Review & Edit actions remain the authoritative save
      // and will surface any real problem.
    }
  };

  const initialTitle =
    DateTime.local().toLocaleString(DateTime.DATE_FULL) === story?.title ? '' : story?.title;
  const persistedCover = !isEmpty(story?.['cover-image']) && story?.['cover-image'];
  const initialCover = persistedCover || (images[0] && { ...images[0], type: 'images' }) || null;
  const isRespondingToRequest = store.get('requestResponse') === 'true';

  const onClose = () => {
    store.set('story_header_ftui_watched', true);
    setFirstTimeUserModal(false)
  }

  return (<>
    {firstTimeUserModal && <Modal
      close={{
        onClose,
      }}
      maxWidth={620}
      closeOnOutsideClick={false}
      isScrollActive={true}
      aboveTooltip
      shadeProps={{ pt: [0, 4] }}
    >
      <Flex m="auto" mb={["200px", "50px"]} flexDirection="column" justifyContent="center" alignItems="center" maxWidth={400}>
        <Flex alignItems="center">
          <Text ml={1} fontFamily="Bitter" fontWeight="normal"
            sx={{
              color: '#333',
              fontFamily: 'Bitter',
              fontSize: '30px',
              fontWeight: '500',
              lineHeight: '165%',
            }}
          >
            Story Header
          </Text>
        </Flex>
        <Text
          color="#64748B"
          fontFamily="Poppins"
          fontSize="18px"
          fontStyle="normal"
          fontWeight={400}
          lineHeight="200%"
          letterSpacing="-0.36px"
          textAlign='center'
          my="30px"
          px="26px"
        >
          Add a title and date to your story <br />
          (it will be printed in the book). {isFromTellAStory ? '' : 'You can always edit later.'}
        </Text>
        <Text
          px="26px"
          color="#64748B"
          fontFamily="Poppins"
          fontSize="18px"
          fontStyle="normal"
          fontWeight={400}
          lineHeight="200%"
          letterSpacing="-0.36px"
          textAlign='center'
          mb="30px"
        >
          {isFromTellAStory ? `Select a preview photo for ${member?.['first-name']} to recognize your story` : 'Select a preview photo to identify stories in your dashboard'}
        </Text>

        {/* {!isFromTellAStory && <Text
          m="auto"
          color="#000"
          fontFamily="Poppins"
          fontSize="18px"
          fontStyle="normal"
          fontWeight={400}
          lineHeight="200%"
          letterSpacing="-0.36px"
          textAlign='center'
          mb={2}
        >
          Example
        </Text>}

        <Image
          maxWidth={240}
          src={ftuiStoryHeader}
          onClick={() => setFirstTimeUserModal(false)}
        /> */}
        <LetsBeginFooterComponent
          sx={{ justifyContent: 'center', }}
          onStepUp={onClose}
          nextButtonText="Okay"
        />
      </Flex>
    </Modal>}
    <Formik
      enableReinitialize
      initialValues={{
        title: initialTitle,
        cover: initialCover,
        'began-on': (story?.['began-on'] && story?.['began-on']) || DateTime.now().toISO(),
        'ended-on': story?.['ended-on'] ? getJSDateFromISO(story?.['ended-on']) : '',
        'journal-id': story?.['journal-id'] || story?.['journal-ids']?.[0],
      }}
      onSubmit={async ({ title, cover, ...values }) => {
        // const isDirty = difference(
        //   Object.values(pick(story, ['began-on', 'ended-on'])),
        //   Object.values(pick(values, ['began-on', 'ended-on']))
        // );
        // if (isDirty) {
        // }
        // delete values?.['began-on'];
        const body = bodyWithLockVersion(story, {
          title: getTitle(title, story.title, story['began-on']),
          ...values,
        });

        await props.updateStory(id, body);
        revalidateStory();
        let fileBlob = null;
        if (isString(cover)) {
          fileBlob = await fetch(cover).then(res => res.blob());
          fileBlob.name = 'story-cover.png';
        }
        if (isObject(cover) && !isEqual(cover, persistedCover)) {
          fileBlob = await fetch(cover['asset-url']).then(res => res.blob());
          const ext = fileExtension(cover['asset-url']);
          fileBlob.name = `story-cover.${ext}`;
        }
        if (fileBlob) {
          await props.createStoryCoverMedium(id, fileBlob);
        }
        if (store.get('requestResponse') === 'true') {
          store.remove('requestResponse');
        }
        revalidateStory();
        getNextRoute();
      }}
    >
      {({ submitForm, values, dirty }) => (
        <Box sx={{ zIndex: 1, bg: 'white', position: 'relative', height: "100vh" }}>
          <AutoSave values={values} dirty={dirty} onSave={autosave} />
          {pathname?.includes('tell_a_story') && <Flex width="100%" p={2} bg="white" justifyContent={'space-between'} alignItems="center" >
            <Flex width={['160px', '300px', "100%"]}>
              <BabysFYLabel />
            </Flex>
          </Flex>}
          <NewStoryHeader
            nextButtonText='Review & Edit'
            onBack={async () => {
              // Awaited so the title/dates/cover are actually saved — and so this
              // replace() runs after onSubmit's own (it calls getNextRoute()
              // internally), rather than being raced and clobbered by it — before
              // navigating away.
              await submitForm()
              if (pathname.includes('/tell_a_story/')) {
                return replace(`/tell_a_story/new/${id}`);
              }
              return replace(`/stories/new/${id}`)
            }}
            title={<Flex alignItems="center">
              <BsBookmarkStar />
              <Text ml={1}>Story Header</Text></Flex>}
            onNext={submitForm}
          />
          <Main mx={[2, 0]} mb={['100px', 0]}>
            <Flex justifyContent="center">
              <Box flex="0 1 600px" mx={[3, 0]}>
                <Field
                  name="title"
                  component={TextField}
                  label="Story Title"
                  my={3}
                  width="100%"
                  placeholder="Tap to enter a title"
                />
                <Flex flexDirection='column' px={[1, 0]} justifyContent="center" alignItems="flex-end" mt={3} mb={4}>
                  <Field
                    component={DatePicker}
                    name="began-on"
                    showError
                    width="100%"
                    label="Story Start Date"
                    maxDate={values['ended-on']}
                  />
                  {(!values['ended-on'] && hideEndDate) ? <Text mr="auto"
                    fontFamily="Poppins"
                    sx={{ cursor: 'pointer' }}
                    color="#9B59B6"
                    fontSize="16px"
                    fontWeight={400}
                    lineHeight="20px"
                    onClick={() => setHideEndDate(false)}
                  >Add story end date</Text>
                    : <Flex mr="auto">
                      <Field
                        component={DatePicker}
                        name="ended-on"
                        showError
                        width="100%"
                        pickerProps={{
                          popperPlacement: 'bottom-end',
                        }}
                        label="Story End Date"
                        minDate={values['ended-on']}
                      />
                      <IoMdClose size={24} onClick={() => setHideEndDate(true)} />
                    </Flex>
                  }
                </Flex>
                <Field
                  name="cover"
                  component={CoverPicker}
                  storyId={id}
                  label="Story Preview Photo"
                  showPreview
                />

                <Field
                  name="cover"
                  component={CoverPicker}
                  storyId={id}
                  label="Select a preview photo"
                  showThumbnails
                />
              </Box>
            </Flex>
          </Main>
          <NewStoryFooter
            onBack={async () => {
              // This is the only "Back" button visible on mobile (the header's is
              // desktop-only) — it used to navigate away without saving at all, so
              // a title/date typed here was silently lost. Save first, same as the
              // header's onBack above.
              await submitForm()
              if (pathname.includes('/tell_a_story/')) {
                return replace(`/tell_a_story/new/${id}/edit`);
              }
              return replace(`/stories/new/${id}/edit`)
            }}
            onNext={submitForm}
            nextButtonText='Review & Edit'
          />
        </Box>
      )}
    </Formik>
  </>
  );
};

NewStoryCover.propTypes = {
  createStoryCoverMedium: PropTypes.func.isRequired,
  updateStory: PropTypes.func.isRequired,
};

export default connect(null, { createStoryCoverMedium, updateStory })(NewStoryCover);
